import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ActionButton, ButtonRow, ChoiceGroup, Row } from '@/components/Form/Form'
import { Lead, Paper, Section } from '@/components/Layout/Layout'
import { Ledger } from '@/components/Ledger/Ledger'
import { format } from '@/i18n/format'
import { useCopy, useLocale } from '@/i18n/useCopy'
import type { AgeBand, Gender, SlotId } from '@/profile/types'
import { AGE_BANDS, GENDERS } from '@/profile/types'
import { useProfileStore } from '@/store/profileStore'
import { extractInterests, extractName, extractRegion } from '@/voice/nlu/extract'
import styles from './Profile.module.css'

/**
 * Everything Namu remembers, and every way to change it.
 *
 * The consent screen promises that all of this can be reviewed, corrected or
 * deleted. This is where that promise is kept, so it is deliberately blunt:
 * one screen, everything on it, nothing behind a submenu.
 *
 * Correction matters more here than anywhere else in the product. Recognisers
 * get names and place names wrong several times more often than ordinary
 * words, and those are exactly the two things this profile is mostly made of.
 */
export function ProfileScreen() {
  const copy = useCopy()
  const locale = useLocale()
  const navigate = useNavigate()

  const profile = useProfileStore((store) => store.profile)
  const savedAt = useProfileStore((store) => store.savedAt)
  const forgetEverything = useProfileStore((store) => store.forgetEverything)

  const [editing, setEditing] = useState<SlotId | null>(null)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const savedLabel =
    savedAt === null
      ? null
      : format(copy.ui.profile.savedOn, {
          date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(savedAt),
        })

  return (
    <Paper title={copy.ui.profile.title} backTo="/">
      <Lead>{copy.ui.profile.subtitle}</Lead>

      <Section>
        <Ledger profile={profile} onEdit={(slot) => setEditing(slot)} />
      </Section>

      {editing ? (
        <Section>
          <SlotEditor slot={editing} onDone={() => setEditing(null)} />
        </Section>
      ) : null}

      <Section>
        {savedLabel ? <Row label={savedLabel} /> : null}

        <ButtonRow>
          <ActionButton variant="primary" onClick={() => navigate('/')}>
            {copy.ui.profile.changeByVoice}
          </ActionButton>

          {confirmingDelete ? (
            <div className={styles.confirm}>
              <p className={styles.confirmTitle}>{copy.ui.profile.deleteConfirmTitle}</p>
              <p className={styles.confirmBody}>{copy.ui.profile.deleteConfirmBody}</p>
              <div className={styles.confirmActions}>
                <ActionButton onClick={() => setConfirmingDelete(false)}>
                  {copy.ui.profile.cancel}
                </ActionButton>
                <ActionButton
                  variant="danger"
                  testId="confirm-delete"
                  onClick={() => {
                    forgetEverything()
                    setConfirmingDelete(false)
                  }}
                >
                  {copy.ui.profile.deleteConfirmAction}
                </ActionButton>
              </div>
            </div>
          ) : (
            <ActionButton variant="danger" onClick={() => setConfirmingDelete(true)}>
              {copy.ui.profile.deleteAll}
            </ActionButton>
          )}
        </ButtonRow>
      </Section>
    </Paper>
  )
}

/**
 * Correcting one answer.
 *
 * The enumerated answers become a row of choices, because picking from four
 * options is faster and more accurate than typing. The open ones get a text
 * field, and are parsed through the same extractors the conversation uses, so
 * "I live in Niamey" typed here behaves exactly as it does when spoken.
 */
function SlotEditor({ slot, onDone }: { slot: SlotId; onDone(): void }) {
  const copy = useCopy()
  const locale = useLocale()
  const store = useProfileStore()
  const profile = store.profile

  const [text, setText] = useState(() => {
    if (slot === 'name') return profile.name?.value ?? ''
    if (slot === 'region') return profile.region?.value ?? ''
    if (slot === 'interests') return profile.interests?.value.join(', ') ?? ''
    return ''
  })

  if (slot === 'ageBand') {
    return (
      <ChoiceGroup<AgeBand>
        label={copy.ui.field.ageBand}
        value={profile.ageBand?.value ?? '25-34'}
        choices={AGE_BANDS.map((band) => ({ value: band, label: copy.ui.ageBand[band] }))}
        onChange={(next) => {
          store.setAgeBand(next, 'chosen')
          onDone()
        }}
      />
    )
  }

  if (slot === 'gender') {
    return (
      <ChoiceGroup<Gender>
        label={copy.ui.field.gender}
        value={profile.gender?.value ?? 'undisclosed'}
        choices={GENDERS.map((gender) => ({ value: gender, label: copy.ui.gender[gender] }))}
        onChange={(next) => {
          store.setGender(next, 'chosen')
          onDone()
        }}
      />
    )
  }

  if (slot === 'language' || slot === 'consent') {
    // Language lives in settings and consent is answered in the conversation.
    onDone()
    return null
  }

  const label =
    slot === 'name'
      ? copy.ui.field.name
      : slot === 'region'
        ? copy.ui.field.region
        : copy.ui.field.interests

  const commit = () => {
    const value = text.trim()
    if (value.length === 0) {
      store.clearSlot(slot)
      onDone()
      return
    }

    if (slot === 'name') {
      store.setName(extractName(value, locale).value ?? value, 'chosen')
    } else if (slot === 'region') {
      store.setRegion(extractRegion(value, locale).value ?? value, 'chosen')
    } else {
      const parsed = extractInterests(value, locale).value
      store.setInterests(parsed ?? [value], 'chosen')
    }
    onDone()
  }

  return (
    <form
      className={styles.editor}
      onSubmit={(event) => {
        event.preventDefault()
        commit()
      }}
    >
      <label className={styles.editorLabel} htmlFor="slot-editor">
        {label}
      </label>
      <input
        id="slot-editor"
        className={styles.editorInput}
        value={text}
        onChange={(event) => setText(event.target.value)}
        autoFocus
        autoComplete="off"
        enterKeyHint="done"
      />
      <div className={styles.editorActions}>
        <ActionButton onClick={onDone}>{copy.ui.profile.cancel}</ActionButton>
        <ActionButton variant="primary" onClick={commit}>
          {copy.ui.ledger.confirm}
        </ActionButton>
      </div>
    </form>
  )
}
